import {
  Box,
  Collapse,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import PendingInsuranceChangeInsuranceModal from "./PendingInsuranceChangeInsuranceModal";
import { IInsuranceProviderRows } from "./index";
import InsuranceService from "Services/InsuranceService/InsuranceService";
import PermissionComponent from "Shared/Common/Permissions/PermissionComponent";
import useLang from "Shared/hooks/useLanguage";
import { AddIcon, RemoveICon } from "Shared/Icons";
import {
  StyledDropButtonThreeDots,
  StyledDropMenuMoreAction,
} from "Utils/Style/Dropdownstyle";
import { reactSelectSMStyle2, stateDropdownArray, styles } from "Utils/Common";
import { savePdfUrls } from "Redux/Actions/Index";

function PendingInsuranceProviderRow(props: {
  row: IInsuranceProviderRows;
  index: number;
  rows: IInsuranceProviderRows[];
  setRows: any;
  handleApprove: (id?: string) => Promise<void>;
  handleReject: (id?: string) => Promise<void>;
  loadGridData: (loader: boolean, reset: boolean) => void;
  selectedProviderIds: number[];
  handleSelectProvider: (providerId: number, checked: boolean) => void;
}) {
  const {
    row,
    handleApprove,
    handleReject,
    loadGridData,
    selectedProviderIds,
    handleSelectProvider,
  } = props;
  const { t } = useLang();
  const dispatch = useDispatch();

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const openDrop = Boolean(anchorEl);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedData, setEditedData] = useState({
    providerCode: row.providerCode || "",
    address: row.address || "",
    city: row.city || "",
    state: row.state || "",
    zipCode: row.zipCode || "",
    phoneNumber: row.phoneNumber || "",
  });

  // State for selected requisition orders and insurance detail modal
  const [selectedRequisitionOrderIds, setSelectedRequisitionOrderIds] =
    useState<string[]>([]);
  const [openInsuranceModal, setOpenInsuranceModal] = useState(false);
  const [insuranceModalRowData, setInsuranceModalRowData] = useState<any>(null);
  const [selectedRequisitionOrders, setSelectedRequisitionOrders] = useState<
    any[]
  >([]);

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApproveClick = () => {
    setShowApproveModal(true);
  };

  const handleRejectClick = () => {
    setShowRejectModal(true);
  };

  const confirmApprove = () => {
    handleApprove(row.id);
    setShowApproveModal(false);
    handleClose();
  };

  const confirmReject = () => {
    // Check if row has any requisitionOrders
    if (row.requisitionOrders && row.requisitionOrders.length > 0) {
      toast.error(
        t(
          "This insurance provider is linked to an order. Update it before proceeding"
        )
      );
      setShowRejectModal(false);
      handleClose();
      return;
    }
    handleReject(row.id);
    setShowRejectModal(false);
    handleClose();
  };

  const getRequisitionCompositeKey = (order: any) =>
    `${order?.requisitionOrderId || 0}_${order?.requisitionId || 0}_${
      order?.requisitonInsurancePatientId || 0
    }`;

  const handleSelectRequisitionOrder = (
    compositeKey: string,
    checked: boolean
  ) => {
    if (checked) {
      setSelectedRequisitionOrderIds((prev) => [...prev, compositeKey]);
    } else {
      setSelectedRequisitionOrderIds((prev) =>
        prev.filter((id) => id !== compositeKey)
      );
    }
  };
  const handleSelectAllRequisitionOrders = (checked: boolean) => {
    if (checked && row.requisitionOrders) {
      const allIds = row.requisitionOrders.map((order) =>
        getRequisitionCompositeKey(order)
      );
      setSelectedRequisitionOrderIds(allIds);
    } else {
      setSelectedRequisitionOrderIds([]);
    }
  };

  const closeInsuranceModal = () => {
    setOpenInsuranceModal(false);
  };

  const handleOpenInsuranceModal = () => {
    if (selectedRequisitionOrderIds.length === 0) {
      toast.error(t("Please select at least one requisition order"));
      return;
    }
    const selectedOrders =
      row.requisitionOrders?.filter((order) =>
        selectedRequisitionOrderIds.includes(getRequisitionCompositeKey(order))
      ) || [];

    if (!selectedOrders.length) {
      toast.error(t("Selected requisition orders not found"));
      return;
    }

    setSelectedRequisitionOrders(selectedOrders);
    const firstOrder = selectedOrders[0];
    setInsuranceModalRowData({
      RequisitionId: firstOrder.requisitionId || 0,
      RequisitionOrderId: firstOrder.requisitionOrderId || 0,
      FacilityId: firstOrder.facilityId || 0,
      TypeId: firstOrder.requisitionTypeId || 0,
      typeId: firstOrder.requisitionTypeId || 0,
      facilityId: firstOrder.facilityId || 0,
    });
    setOpenInsuranceModal(true);
  };

  useEffect(() => {
    if (!open) {
      setSelectedRequisitionOrderIds([]);
      setSelectedRequisitionOrders([]);
    }
  }, [open]);

  const handleEditClick = () => {
    setIsEditMode(true);
    setEditedData({
      providerCode: row.providerCode || "",
      address: row.address || "",
      city: row.city || "",
      state: row.state || "",
      zipCode: row.zipCode || "",
      phoneNumber: row.phoneNumber || "",
    });
    handleClose();
  };

  const handleEditInputChange = (name: string, value: string) => {
    setEditedData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveEdit = async () => {
    // Validation for required fields
    if (!editedData.address?.trim()) {
      toast.error(t("Address is required"));
      return;
    }
    if (!editedData.city?.trim()) {
      toast.error(t("City is required"));
      return;
    }
    if (!editedData.state?.trim()) {
      toast.error(t("State is required"));
      return;
    }
    if (!editedData.zipCode?.trim()) {
      toast.error(t("Zip Code is required"));
      return;
    }

    try {
      const payload = {
        insuranceAssignmentId: row.insuranceAssignmentId,
        providerId: parseInt(row.id),
        providerName: row.providerName,
        providerCode: editedData.providerCode,
        address: editedData.address,
        city: editedData.city,
        state: editedData.state,
        zipCode: editedData.zipCode,
        phoneNumber: editedData.phoneNumber,
        recordID: row.recordId,
        image: row.file || "",
        insuranceStatus: "Pending",
      };

      await InsuranceService.InsuranceApprovalEdit(payload);

      toast.success(t("Insurance provider updated successfully"));
      setIsEditMode(false);

      // Reload grid data to reflect changes
      loadGridData(true, false);
    } catch (error: any) {
      console.error("Error updating insurance provider:", error);
      toast.error(
        error?.response?.data?.message ||
          t("Failed to update insurance provider")
      );
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedData({
      providerCode: row.providerCode || "",
      address: row.address || "",
      city: row.city || "",
      state: row.state || "",
      zipCode: row.zipCode || "",
      phoneNumber: row.phoneNumber || "",
    });
  };

  return (
    <>
      <PendingInsuranceChangeInsuranceModal
        open={openInsuranceModal}
        onClose={closeInsuranceModal}
        rowData={insuranceModalRowData}
        selectedRequisitionOrders={selectedRequisitionOrders}
        loadGridData={loadGridData}
      />
      <Modal
        show={showApproveModal}
        onHide={() => setShowApproveModal(false)}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton className="bg-light-primary m-0 p-5">
          <h4>{t("Approve Insurance Provider")}</h4>
        </Modal.Header>
        <Modal.Body>
          {t("Are you sure you want to approve this insurance provider?")}
        </Modal.Body>
        <Modal.Footer className="p-0">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowApproveModal(false)}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className="btn btn-success m-2"
            onClick={confirmApprove}
          >
            {t("Approve")}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showRejectModal}
        onHide={() => setShowRejectModal(false)}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton className="bg-light-primary m-0 p-5">
          <h4>{t("Reject Insurance Provider")}</h4>
        </Modal.Header>
        <Modal.Body>
          {t("Are you sure you want to reject this insurance provider?")}
        </Modal.Body>
        <Modal.Footer className="p-0">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowRejectModal(false)}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className="btn btn-danger m-2"
            onClick={confirmReject}
          >
            {t("Reject")}
          </button>
        </Modal.Footer>
      </Modal>

      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <div className="d-flex justify-content-center">
            <IconButton
              aria-label="expand row"
              size="small"
              className="btn btn-icon btn-icon-light btn-sm fw-bold btn-table-expend-row rounded h-20px w-20px min-h-20px me-2"
              onClick={() => setOpen(!open)}
            >
              {open ? (
                <button
                  id={`PendingInsuranceProviderHide_${row.id}`}
                  className="btn btn-icon btn-icon-light btn-sm fw-bold btn-table-expend-row rounded h-20px w-20px min-h-20px"
                >
                  <RemoveICon />
                </button>
              ) : (
                <button
                  id={`PendingInsuranceProviderShow_${row.id}`}
                  className="btn btn-icon btn-icon-light btn-sm fw-bold btn-primary rounded h-20px w-20px min-h-20px"
                >
                  <AddIcon />
                </button>
              )}
            </IconButton>
          </div>
        </TableCell>
        <TableCell style={{ width: "49px" }}>
          <label className="form-check form-check-sm form-check-solid d-flex justify-content-center">
            <input
              id={`PendingInsuranceProviderCheckBox_${row.id}`}
              className="form-check-input"
              type="checkbox"
              checked={selectedProviderIds.includes(parseInt(row.id))}
              onChange={(e) =>
                handleSelectProvider(parseInt(row.id), e.target.checked)
              }
            />
          </label>
        </TableCell>
        <TableCell>
          <div className="d-flex justify-content-center">
            {isEditMode ? (
              <div className="gap-2 d-flex">
                <button
                  id={`BloodLisStorageTypeSave`}
                  className="btn btn-icon btn-sm fw-bold btn-table-save btn-icon-light h-32px w-32px fas-icon-20px"
                  onClick={handleSaveEdit}
                >
                  <i className="bi bi-check2" />
                </button>
                <button
                  id={`BloodLisStorageTypeCancel`}
                  className="btn btn-icon btn-sm fw-bold btn-table-cancel btn-icon-light h-32px w-32px fas-icon-20px"
                  onClick={handleCancelEdit}
                >
                  <i className="bi bi-x" />
                </button>
              </div>
            ) : (
              <>
                <StyledDropButtonThreeDots
                  id={`PendingInsuranceProviderActions_${row.id}`}
                  aria-controls={openDrop ? "demo-positioned-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={openDrop ? "true" : undefined}
                  onClick={handleClick}
                  className="btn btn-light-info btn-sm btn-icon moreactions min-w-auto rounded-4"
                >
                  <i className="bi bi-three-dots-vertical p-0 icon"></i>
                </StyledDropButtonThreeDots>
                <StyledDropMenuMoreAction
                  aria-labelledby="demo-positioned-button"
                  anchorEl={anchorEl}
                  open={openDrop}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                >
                  <PermissionComponent
                    moduleName="Setup"
                    pageName="Insurance Provider Request"
                    permissionIdentifier="Edit"
                  >
                    <MenuItem onClick={handleClose} className="p-0">
                      <a
                        id={`PendingInsuranceProviderEdit_${row.id}`}
                        onClick={() => {
                          handleClose();
                          handleEditClick();
                        }}
                      >
                        <i className="fa fa-edit text-success mr-2 w-20px"></i>
                        {t("Edit")}
                      </a>
                    </MenuItem>
                  </PermissionComponent>

                  <PermissionComponent
                    moduleName="Setup"
                    pageName="Insurance Provider Request"
                    permissionIdentifier="Approve"
                  >
                    <MenuItem onClick={handleClose} className="p-0">
                      <a
                        id={`PendingInsuranceProviderApprove_${row.id}`}
                        onClick={() => {
                          handleClose();
                          handleApproveClick();
                        }}
                      >
                        <i className="fa fa-check text-success mr-2"></i>
                        {t("Approve")}
                      </a>
                    </MenuItem>
                  </PermissionComponent>
                  <PermissionComponent
                    moduleName="Setup"
                    pageName="Insurance Provider Request"
                    permissionIdentifier="Reject"
                  >
                    <MenuItem onClick={handleClose} className="p-0">
                      <a
                        id={`PendingInsuranceProviderReject_${row.id}`}
                        onClick={() => {
                          handleClose();
                          handleRejectClick();
                        }}
                      >
                        <i className="fa fa-times text-danger mr-2"></i>
                        {t("Reject")}
                      </a>
                    </MenuItem>
                  </PermissionComponent>
                </StyledDropMenuMoreAction>
              </>
            )}
          </div>
        </TableCell>
        <TableCell id={`RowProviderName_${row.id}`}>
          {row.providerName}
        </TableCell>
        <TableCell id={`RowProviderCode_${row.id}`}>
          {isEditMode ? (
            <input
              type="text"
              name="providerCode"
              className="form-control bg-white h-30px rounded-2 fs-8"
              placeholder={t("Provider Code")}
              value={editedData.providerCode}
              onChange={(e) =>
                handleEditInputChange(e.target.name, e.target.value)
              }
            />
          ) : (
            row.providerCode || "-"
          )}
        </TableCell>
        <TableCell id={`RowAddress_${row.id}`}>
          {isEditMode ? (
            <input
              type="text"
              name="address"
              className="form-control bg-white h-30px rounded-2 fs-8"
              placeholder={t("Address")}
              value={editedData.address}
              onChange={(e) =>
                handleEditInputChange(e.target.name, e.target.value)
              }
              required
            />
          ) : (
            row.address
          )}
        </TableCell>
        <TableCell id={`RowCity_${row.id}`}>
          {isEditMode ? (
            <input
              type="text"
              name="city"
              className="form-control bg-white h-30px rounded-2 fs-8"
              placeholder={t("City")}
              value={editedData.city}
              onChange={(e) =>
                handleEditInputChange(e.target.name, e.target.value)
              }
              required
            />
          ) : (
            row.city
          )}
        </TableCell>
        <TableCell id={`RowState_${row.id}`}>
          {isEditMode ? (
            <Select
              menuPortalTarget={document.body}
              styles={reactSelectSMStyle2("30px")}
              theme={(theme) => styles(theme)}
              options={stateDropdownArray}
              name="state"
              placeholder={t("Select State")}
              value={stateDropdownArray.find(
                (option) => option.value === editedData.state
              )}
              onChange={(option) => {
                handleEditInputChange("state", option ? option.value : "");
              }}
              isSearchable={true}
              isClearable={false}
              className="z-index-3"
            />
          ) : (
            row.state
          )}
        </TableCell>
        <TableCell id={`RowZipCode_${row.id}`}>
          {isEditMode ? (
            <input
              type="text"
              name="zipCode"
              className="form-control bg-white h-30px rounded-2 fs-8"
              placeholder={t("Zip Code")}
              value={editedData.zipCode}
              onChange={(e) =>
                handleEditInputChange(e.target.name, e.target.value)
              }
              required
            />
          ) : (
            row.zipCode
          )}
        </TableCell>
        <TableCell id={`RowPhoneNumber_${row.id}`}>
          {isEditMode ? (
            <input
              type="text"
              name="phoneNumber"
              className="form-control bg-white h-30px rounded-2 fs-8"
              placeholder={t("Phone Number")}
              value={editedData.phoneNumber}
              onChange={(e) =>
                handleEditInputChange(e.target.name, e.target.value)
              }
            />
          ) : (
            row.phoneNumber || "-"
          )}
        </TableCell>
        {/* <TableCell id={`RowRecordId_${row.id}`}>{row.recordId}</TableCell> */}
        <TableCell id={`RowFile_${row.id}`}>
          <div className="d-flex justify-content-center">
            {row.file ? (
              <Link to={`/docs-viewer`} target="_blank">
                <i
                  className="bi bi-file-earmark-pdf text-danger fa-2x cursor-pointer"
                  onClick={() => {
                    dispatch(savePdfUrls(row.file));
                  }}
                ></i>
              </Link>
            ) : (
              "-"
            )}
          </div>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={11} className="padding-0">
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography gutterBottom component="div">
                <div className="row">
                  <div className="col-lg-12 bg-white px-lg-14 pb-6 table-expend-sticky">
                    {/* <div className="card shadow-sm rounded border border-warning mt-3"> */}
                    {/* <div className="card-header d-flex justify-content-between align-items-center bg-light-secondary min-h-35px">
                        <h6>{t("Requisition Orders")}</h6>
                      </div> */}
                    <div className="card-body py-md-4 py-3">
                      {row.requisitionOrders &&
                      row.requisitionOrders.length > 0 ? (
                        <>
                          <div className="mb-4 d-flex align-items-center gap-3">
                            <button
                              type="button"
                              className="btn btn-primary btn-sm fw-500"
                              onClick={handleOpenInsuranceModal}
                            >
                              {t("Change Insurance Detail")}
                            </button>
                          </div>

                          <TableContainer>
                            <Table
                              size="small"
                              className="provider-insurance-order-table table-bordered mb-1"
                            >
                              <TableHead>
                                <TableRow>
                                  <TableCell>
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      checked={
                                        row.requisitionOrders &&
                                        row.requisitionOrders.length > 0 &&
                                        selectedRequisitionOrderIds.length ===
                                          row.requisitionOrders.length
                                      }
                                      onChange={(e) =>
                                        handleSelectAllRequisitionOrders(
                                          e.target.checked
                                        )
                                      }
                                      style={{
                                        cursor: "pointer",
                                        width: "16px",
                                        height: "16px",
                                        marginTop: "2px",
                                        marginRight: "6px",
                                        flexShrink: 0,
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>{t("Requisition Type")}</TableCell>
                                  <TableCell>{t("Record ID")}</TableCell>
                                  <TableCell>{t("Accession Number")}</TableCell>
                                  <TableCell>{t("Lab Name")}</TableCell>
                                  <TableCell>{t("Facility Name")}</TableCell>
                                  <TableCell>
                                    {t("Insurance Provider Name")}
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {row.requisitionOrders.map((order, index) => (
                                  <TableRow
                                    key={
                                      order.requisitonInsurancePatientId ||
                                      index
                                    }
                                  >
                                    <TableCell>
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={selectedRequisitionOrderIds.includes(
                                          getRequisitionCompositeKey(order)
                                        )}
                                        onChange={(e) =>
                                          handleSelectRequisitionOrder(
                                            getRequisitionCompositeKey(order),
                                            e.target.checked
                                          )
                                        }
                                        style={{
                                          cursor: "pointer",
                                          width: "16px",
                                          height: "16px",
                                          marginTop: "2px",
                                          marginRight: "6px",
                                          flexShrink: 0,
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      {order.requisitionType || "-"}
                                    </TableCell>
                                    <TableCell>
                                      {order.recordId || "-"}
                                    </TableCell>
                                    <TableCell>
                                      {order.accessionNo || "-"}
                                    </TableCell>
                                    <TableCell>
                                      {order.labName || "-"}
                                    </TableCell>
                                    <TableCell>
                                      {order.facilityName || "-"}
                                    </TableCell>
                                    <TableCell>
                                      {row.providerName || "-"}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </>
                      ) : (
                        <div className="text-center py-4">
                          <span>{t("No requisition orders found")}</span>
                        </div>
                      )}
                    </div>
                    {/* </div> */}
                  </div>
                </div>
              </Typography>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default PendingInsuranceProviderRow;
